import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import {
  defaultWeekFor,
  loadSeasonCatalogue,
  loadSeasonWeeks,
  REGULAR_SEASON_TYPE_ID,
  seasonSpanLabel,
  weekHeading,
} from "../services/seasons/seasonService";
import { getProviderInfo } from "../services/sportsData";

/**
 * Which season the reader is looking at.
 *
 * THE ONE RULE THIS HOOK EXISTS TO KEEP
 * -------------------------------------
 * When the answer is "the current season", which is the answer on every
 * first visit, this hook hands back `scheduleSeason: null` and the page
 * below it behaves exactly as it did before seasons existed. Not
 * approximately: `useSchedule(null)` takes the identical branch, makes the
 * identical request and reads the identical cache key. Everything else in
 * this file is inert until somebody actively picks a different year.
 *
 * That is also why nothing here blocks. The catalogue is fetched in the
 * background and the picker appears when it arrives. If ESPN's season
 * endpoints are down, `catalogueError` is set, no picker renders, and the
 * schedule is completely unaffected.
 *
 * WHERE THE CHOICE LIVES, AND WHY IN TWO PLACES
 * ---------------------------------------------
 * In the URL, because `/games?season=2015&type=2&week=1` is a link
 * somebody can send to a friend, and because that is already how this app
 * scopes a list: `/learn/players?team=KC` does the same thing for the same
 * reason (see `toPlayers` in `common/navigation.js`).
 *
 * And in localStorage, because a reader who went to look at 2015 and then
 * reloaded meant to still be in 2015. The URL wins whenever it says
 * anything, so a shared link never lands somebody in the season they were
 * reading yesterday. Storage only fills in a bare season-aware page.
 *
 * WEEK NAVIGATION IS FOR PAST SEASONS ONLY
 * ----------------------------------------
 * The current season keeps the rolling "what is on around now" view it has
 * always had, which is the view that knows about live games, kickoffs and
 * the practice game. Pinning it to a week would be a different screen with
 * different behaviour, and the brief for this work was that the current
 * season must not get one. Asking for `?season=<current>&week=3` therefore
 * lands on the ordinary current-season view rather than an error.
 */

const STORAGE_KEY = "idkf.season";

const PARAM_SEASON = "season";
const PARAM_TYPE = "type";
const PARAM_WEEK = "week";

const SeasonSelectionContext = createContext(null);

/**
 * Where a past season opens before its calendar has arrived.
 *
 * Not a claim about the data, a place to start looking: the request for
 * regular season week 1 is the same request the calendar itself is read
 * out of, so the two share one response and the archive paints from a
 * single fetch. Once the calendar lands, `defaultWeekFor` replaces this
 * with the season's real opening week. For every season this product
 * offers, back to 2001, the two are the same week.
 */
const OPENING_WEEK = Object.freeze({
  seasonType: REGULAR_SEASON_TYPE_ID,
  week: 1,
});

/** A year is four digits. Anything else in the address bar is ignored. */
const yearParam = (raw) => {
  const value = Number(String(raw ?? "").trim());
  return Number.isInteger(value) && value >= 1900 && value <= 2200 ? value : null;
};

const smallIntParam = (raw, max) => {
  const value = Number(String(raw ?? "").trim());
  return Number.isInteger(value) && value >= 1 && value <= max ? value : null;
};

export function readStoredSeason() {
  try {
    return yearParam(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function storeSeason(year) {
  try {
    if (year == null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, String(year));
  } catch {
    /* private mode: the choice still holds for this session */
  }
}

export function useSeasonSelectionState({ enabled = true } = {}) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const [catalogue, setCatalogue] = useState(null);
  const [catalogueError, setCatalogueError] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [weeksError, setWeeksError] = useState(null);

  /* ------------------------------------------------------ catalogue */

  /**
   * A build running on the practice game has no seasons.
   *
   * The practice game is a script with a clock and no history, so there is
   * nothing for a picker to offer and nothing an older year could load.
   * Offering one anyway would be a control that only ever produces an
   * error, so the catalogue is not even fetched: `REACT_APP_SPORTS_PROVIDER
   * =mock` gets the Games page exactly as it was, which is also what the
   * whole test suite runs on.
   */
  const browsesSeasons = enabled && !getProviderInfo().isSimulated;

  useEffect(() => {
    if (!browsesSeasons) {
      setCatalogue(null);
      setCatalogueError(null);
      return undefined;
    }

    let cancelled = false;

    loadSeasonCatalogue()
      .then(({ catalogue: loaded }) => {
        if (!cancelled) setCatalogue(loaded);
      })
      .catch((error) => {
        // No picker, and nothing else changes. Recorded rather than
        // thrown: a season list that cannot be fetched is not a reason to
        // fail a page whose job is showing football.
        if (!cancelled) setCatalogueError(error);
      });

    return () => {
      cancelled = true;
    };
  }, [browsesSeasons]);

  const currentYear = enabled ? (catalogue?.current?.year ?? null) : null;

  /* ------------------------------------------------------ selection */

  const requestedYear = yearParam(params.get(PARAM_SEASON));

  /**
   * A bare season-aware page adopts the season this browser was last
   * reading, and says so in the address bar so the page can be shared from
   * there.
   *
   * Only when the URL is silent, and never for the current season: that one
   * is the default and the default has no parameters. Until the catalogue
   * lands we cannot tell which case we are in, so nothing happens at all,
   * which is the same as doing nothing.
   */
  useEffect(() => {
    if (!enabled || requestedYear != null || currentYear == null) return;

    const storedYear = readStoredSeason();
    if (storedYear == null || storedYear === currentYear) return;
    if (!catalogue.years.includes(storedYear)) return;

    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(PARAM_SEASON, String(storedYear));
        return next;
      },
      { replace: true }
    );
  }, [
    catalogue,
    currentYear,
    enabled,
    location.pathname,
    location.search,
    requestedYear,
    setParams,
  ]);

  /**
   * The season actually being shown.
   *
   * Anything the catalogue does not offer falls back to the current
   * season rather than erroring: a hand-edited or stale URL should land
   * somebody on football, not on a message about a year.
   */
  const selectedYear =
    enabled &&
    currentYear != null &&
    requestedYear != null &&
    requestedYear !== currentYear &&
    (catalogue?.years ?? []).includes(requestedYear)
      ? requestedYear
      : currentYear;

  const isArchive = selectedYear != null && selectedYear !== currentYear;

  /* ---------------------------------------------------------- weeks */

  const requestedType = smallIntParam(params.get(PARAM_TYPE), 4);
  const requestedWeek = smallIntParam(params.get(PARAM_WEEK), 25);

  /**
   * Which week to read the calendar out of.
   *
   * Whatever the URL asked for, so that the calendar request and the
   * fixtures request are the same URL and share one response. Taken from
   * the address bar rather than from the calendar, which is what keeps
   * this out of a loop with the value it produces.
   */
  const calendarType = requestedType ?? OPENING_WEEK.seasonType;
  const calendarWeek = requestedWeek ?? OPENING_WEEK.week;

  useEffect(() => {
    if (!enabled || !isArchive) {
      setWeeks(null);
      setWeeksError(null);
      return undefined;
    }

    let cancelled = false;
    setWeeksError(null);

    loadSeasonWeeks(selectedYear, {
      seasonType: calendarType,
      week: calendarWeek,
    })
      .then(({ weeks: loaded }) => {
        if (!cancelled) setWeeks(loaded);
      })
      .catch((error) => {
        if (!cancelled) setWeeksError(error);
      });

    return () => {
      cancelled = true;
    };
    // Only the season decides whether to refetch. The week is a detail of
    // which response the calendar is read out of, and every week of a
    // season carries the same one, so changing week must not refetch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isArchive, selectedYear]);

  /**
   * Memoised rather than derived inline, because everything downstream
   * keys off it: a fresh empty array on every render would restart the
   * week lookup and the schedule request behind it on every render.
   */
  const phases = useMemo(() => weeks?.phases ?? [], [weeks]);

  /**
   * Which week of the archive to show.
   *
   * The URL wins when it names a week this season actually has. Otherwise
   * the season opens where a person would expect it to: the first week of
   * the regular season, taken from the calendar rather than assumed, since
   * a season's shape moves (2015 had seventeen regular weeks and five
   * preseason ones; 2025 had eighteen and four).
   */
  const fallbackWeek = useMemo(() => defaultWeekFor(phases), [phases]);

  const hasCalendar = phases.length > 0;

  /**
   * A week named in the URL is trusted until the calendar can check it.
   *
   * Without that, a shared link to the Super Bowl would load the opening
   * week first and jump, which is two requests and a flicker. Once the
   * calendar is here a week that season never had loses, and the reader
   * lands on the opening week instead.
   */
  const requestedIsReal =
    requestedType != null &&
    requestedWeek != null &&
    (!hasCalendar ||
      phases.some(
        (phase) =>
          phase.seasonTypeId === requestedType &&
          phase.weeks.some((entry) => entry.number === requestedWeek)
      ));

  const activeWeek = useMemo(
    () =>
      requestedIsReal
        ? { seasonType: requestedType, week: requestedWeek }
        : (fallbackWeek ?? OPENING_WEEK),
    [requestedIsReal, requestedType, requestedWeek, fallbackWeek]
  );

  /* -------------------------------------------------------- actions */

  const selectSeason = useCallback(
    (year) => {
      if (!enabled) return;

      const value = yearParam(year);
      storeSeason(value === currentYear ? null : value);

      setParams((previous) => {
        const next = new URLSearchParams(previous);
        // Back to the default view: no parameters at all, which is exactly
        // the URL somebody arriving for the first time gets.
        next.delete(PARAM_TYPE);
        next.delete(PARAM_WEEK);

        if (value == null || value === currentYear) next.delete(PARAM_SEASON);
        else next.set(PARAM_SEASON, String(value));

        return next;
      });
    },
    [currentYear, enabled, setParams]
  );

  const selectWeek = useCallback(
    (seasonType, week) => {
      if (!enabled) return;

      setParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set(PARAM_TYPE, String(seasonType));
        next.set(PARAM_WEEK, String(week));
        return next;
      });
    },
    [enabled, setParams]
  );

  /* --------------------------------------------------------- result */

  /**
   * What `useSchedule` is given. Null for the current season, which is the
   * whole point: null means the untouched path.
   */
  const scheduleSeason = useMemo(() => {
    if (!isArchive || !activeWeek) return null;
    return {
      year: selectedYear,
      seasonType: activeWeek.seasonType,
      week: activeWeek.week,
      isArchive: true,
    };
  }, [isArchive, activeWeek, selectedYear]);

  const span = isArchive ? (weeks?.span ?? null) : (catalogue?.current ?? null);

  return {
    /** Every selectable year, newest first. Empty until the list lands. */
    years: enabled ? (catalogue?.years ?? []) : [],
    currentYear,
    selectedYear,
    isArchive,
    /** True while a past season is picked but its weeks have not arrived. */
    weeksPending: isArchive && phases.length === 0 && weeksError == null,
    catalogueError,
    weeksError,

    phases,
    activeWeek,
    /** "Postseason: Super Bowl", named by ESPN rather than by us. */
    weekHeading: activeWeek
      ? weekHeading(phases, activeWeek.seasonType, activeWeek.week)
      : null,
    /** "Aug 2026 to Feb 2027". The answer to which season this is. */
    spanLabel: seasonSpanLabel(span),

    scheduleSeason,
    selectSeason,
    selectWeek,
  };
}

export function SeasonSelectionProvider({ enabled = true, children }) {
  const value = useSeasonSelectionState({ enabled });

  return (
    <SeasonSelectionContext.Provider value={value}>
      {children}
    </SeasonSelectionContext.Provider>
  );
}

export default function useSeasonSelection() {
  const context = useContext(SeasonSelectionContext);
  const fallback = useSeasonSelectionState({ enabled: context == null });

  return context ?? fallback;
}
